<template>
  <div>
    <label class="block text-xs font-semibold text-typography-primary font-geist py-[6px]">
      {{ label }}
    </label>
    <div
      :class="{
        'flex items-center gap-4 flex-wrap': multiple && displayImages.length > 0,
      }"
    >
      <div v-for="(image, index) in displayImages" :key="index">
        <div
          v-if="image.type === 'image'"
          :key="image.url"
          class="h-[114px] w-[114px] rounded-lg cursor-zoom-in relative"
          :style="{
            backgroundImage: `url(${image.url})`,
            backgroundSize: 'cover',
          }"
          @click="openLightbox(index)"
        >
          <div
            class="bg-neutral-white rounded-full flex items-center justify-center absolute -right-[12px] p-2 -top-[12px] cursor-pointer shadow"
            @click.stop.prevent="removePhoto(index)"
          >
            <LineTimes class="h-3 w-3 fill-typography-primary" />
          </div>
        </div>
        <div v-else-if="image.type === 'font'" class="relative">
          <div class="bg-neutral-100 rounded-lg p-3 border border-neutral-200">
            <div class="text-xs text-typography-secondary mb-1">Font File</div>
            <div class="text-sm text-typography-primary font-mono break-all">
              {{ image.name || image.url || 'Unknown font' }}
            </div>
            <div
              class="bg-neutral-white rounded-full flex items-center justify-center absolute -right-[12px] p-2 -top-[12px] cursor-pointer shadow"
              @click.stop.prevent="removePhoto(index)"
            >
              <LineTimes class="h-3 w-3 fill-typography-primary" />
            </div>
          </div>
        </div>
        <div v-else class="relative">
          <ChatAttachment
            :type="'file'"
            :src="image.url"
            :metadata="{
              name: image.name ?? image.url?.split('/').pop() ?? 'Unknown file',
              extension: image.type,
            }"
          >
            <div
              class="bg-neutral-white rounded-full flex items-center justify-center absolute -right-[12px] p-2 -top-[12px] cursor-pointer shadow text-typography-primary"
              @click.stop.prevent="removePhoto(index)"
            >
              <LineTimes class="h-3 w-3 fill-typography-primary" />
            </div>
          </ChatAttachment>
        </div>
      </div>
      <DropArea
        v-if="(multiple && displayImages.length < max) || displayImages.length === 0"
        v-model="files"
        :class="{
          '!w-[114px] flex-grow mr-6': multiple && displayImages.length > 0,
        }"
        :accept="accept"
        :error="error"
        :max="max"
        :multiple="multiple"
        @change="handleAddedFiles"
      />
    </div>
    <span v-if="hint" class="text-typography-disabled text-xs">
      {{ hint }}
    </span>

    <PulseLightbox
      :imgs="displayImages.filter(img => img.type === 'image').map(img => img.url)"
      :visible="showLightbox"
      :index="lightboxIndex"
      @hide="showLightbox = false"
    />
  </div>
</template>

<script setup lang="ts">
import { LineTimes } from '@pulse/components/icons';

const showLightbox = ref(false);
const lightboxIndex = ref(0);

const props = defineProps({
  accept: {
    default: 'image/*',
    type: String,
  },
  error: {
    default: false,
    type: Boolean,
  },
  hint: {
    default: '',
    type: String,
  },
  images: {
    default: () => [],
    type: Array as PropType<string[]>,
  },
  label: {
    default: '',
    type: String,
  },
  max: {
    default: 2,
    type: Number,
  },
  multiple: {
    default: false,
    type: Boolean,
  },
});

const files = ref(
  Array.isArray(props.images)
    ? props.images.filter(item => Boolean(item) && item !== '')
    : [props.images].filter(item => Boolean(item) && item !== ''),
);

function createImageDisplay(file: string) {
  const extension = file?.split('.')?.pop()?.toLowerCase();
  const isFont = extension && ['woff', 'woff2', 'ttf', 'otf', 'eot'].includes(extension);

  return {
    name: file?.split('/').pop(),
    type: isFont ? 'font' : 'image',
    url: file,
  };
}

const displayImages = computed(() => {
  if (!Array.isArray(files.value)) {
    return [];
  }

  return Array.from(files.value)
    .map(file => {
      if (!file) {
        return null;
      }

      if (typeof file === 'string' && file === '') {
        return null;
      }

      if (typeof file === 'string') {
        return createImageDisplay(file);
      }

      const fileObj = file as File;
      const extension = fileObj.name?.split('.')?.pop()?.toLowerCase();
      const isFont = extension && ['woff', 'woff2', 'ttf', 'otf', 'eot'].includes(extension);
      const isFontMime =
        fileObj.type &&
        ['font/woff', 'font/woff2', 'font/ttf', 'font/otf', 'application/font-woff', 'application/font-woff2'].includes(
          fileObj.type,
        );

      return {
        name: fileObj.name,
        type: isFont || isFontMime ? 'font' : fileObj.type.includes('image') ? 'image' : 'file',
        url: URL.createObjectURL(fileObj),
      };
    })
    .filter(item => item !== null) as { url: string; type: string; name?: string }[];
});

const emit = defineEmits(['update']);

watch(
  () => props.images,
  newImages => {
    if (Array.isArray(newImages)) {
      files.value = newImages.filter(item => Boolean(item) && item !== '');
    } else {
      files.value = [newImages].filter(item => Boolean(item) && item !== '');
    }
  },
  { immediate: true },
);

function openLightbox(index: number) {
  const imageOnlyIndex = displayImages.value.slice(0, index + 1).filter(img => img.type === 'image').length - 1;
  lightboxIndex.value = imageOnlyIndex;
  showLightbox.value = true;
}

function handleAddedFiles(addedFiles) {
  emit('update', files.value, { action: 'add', files: addedFiles }); // addedFiles is already added via v-model
}

function removePhoto(index: number) {
  const newImages = files.value.filter((_, i) => i !== index);

  files.value = newImages;
  emit('update', files.value, { action: 'remove', index });
}
</script>
